import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Input, Tabs } from '@fikasio/react-ui-components';
import { useTranslation } from 'react-i18next';
import ObjectivesView from 'components/objectives/ObjectivesView';
import TasksView from 'components/tasks/TasksView';
import { DEFAULT_TASKS_FILTER_DRAFT, TasksFilterDraft } from 'components/tasks/utils';
import PaneHeader from 'components/UI/PaneHeader';
import { FEATURES } from 'services/features/catalog';
import { useFeatureEnabled } from 'services/features/hooks';
import { useGetObjectivesQuery } from 'services/objectives/api';
import { setPaneContent } from 'services/pane/slice';
import { useAddProjectMutation, useGetProjectsQuery, usePatchProjectMutation } from 'services/projects/api';
import { Project, ProjectMember } from 'services/projects/types';
import { useGetTasksQuery } from 'services/tasks/api';
import { CLOSED_TASK_STATUSES } from 'services/tasks/types';
import links, { linkKey, matchesLinkKey } from 'utils/links';
import ProjectDiscussion from './ProjectDiscussion';
import ProjectInformationsForm from './ProjectInformationsForm';
import ProjectMembers from './ProjectMembers';
import ProjectStats from './ProjectStats';

interface ProjectPaneProps {
  defaultTab?: string | false;
  id: string;
  showExpandIcon?: boolean;
}

const projectTasksFilter: TasksFilterDraft = {
  ...DEFAULT_TASKS_FILTER_DRAFT,
  completion: 'ALL',
  includeArchived: true,
};

const ProjectPane = ({
  defaultTab,
  id,
  showExpandIcon = true,
}: ProjectPaneProps) => {
  const { enabled: objectivesEnabled } = useFeatureEnabled(FEATURES.objectives);
  const { enabled: tasksEnabled } = useFeatureEnabled(FEATURES.tasks);
  const { data: objectives } = useGetObjectivesQuery(undefined, { skip: !objectivesEnabled });
  const { data: projects } = useGetProjectsQuery();
  const listedProject = (projects || []).find(item => matchesLinkKey(item, id));
  const [createdProject, setCreatedProject] = useState<{ paneId: string; project: Project }>();
  const created = createdProject?.paneId === id ? createdProject.project : undefined;
  const project = listedProject
    ?? (created && (projects || []).find(item => item.id === created.id))
    ?? created;
  const [createProject] = useAddProjectMutation();
  const [patchProject] = usePatchProjectMutation();
  const nameRef = useRef(project?.name ?? '');
  const nameTouchedRef = useRef(false);
  const creatingRef = useRef<Promise<Project | undefined> | null>(null);
  const [editedMembers, setEditedMembers] = useState<ProjectMember[] | undefined>(undefined);
  const [editedMembersPaneId, setEditedMembersPaneId] = useState(id);
  if (editedMembersPaneId !== id) {
    setEditedMembersPaneId(id);
    setEditedMembers(undefined);
  }
  const { data: incompleteTasks } = useGetTasksQuery({
    filter: {
      isArchived: false,
      projects: project?.id,
      status: { $nin: [...CLOSED_TASK_STATUSES] },
    },
    limit: 1,
  }, { skip: !tasksEnabled || !project?.id });
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    nameTouchedRef.current = false;
    creatingRef.current = null;
  }, [id]);

  useEffect(() => {
    if (nameTouchedRef.current) {
      return;
    }
    nameRef.current = project?.name ?? '';
  }, [id, project?.name]);

  const createProjectFromTitle = (name: string) => {
    if (project?.id) {
      return Promise.resolve(project);
    }
    if (creatingRef.current) {
      return creatingRef.current;
    }
    if (!name) {
      return Promise.resolve(undefined);
    }
    creatingRef.current = createProject({ name })
      .then(({ data }) => {
        if (data) {
          setCreatedProject({ paneId: id, project: data });
          return data;
        }
        creatingRef.current = null;
        return undefined;
      });
    return creatingRef.current;
  };
  const projectTasksIncompleteCount = incompleteTasks?.total ?? 0;
  const projectObjectives = objectivesEnabled
    ? objectives?.filter(objective => objective.projects.some(op => op.id === project?.id))
    : undefined;
  const requestedTab = defaultTab || (tasksEnabled ? 'tasks' : 'objectives');
  let initialTab = requestedTab;
  if (!tasksEnabled && initialTab === 'tasks') {
    initialTab = objectivesEnabled ? 'objectives' : 'infos';
  }
  if (!objectivesEnabled && initialTab === 'objectives') {
    initialTab = 'infos';
  }

  return (
    <>
      <PaneHeader
        expandTo={project?.id && project.id !== 'NEW' ? links.project(linkKey(project)) : undefined}
        showExpandIcon={showExpandIcon}
      >
        <Input
          defaultValue={project?.name ?? ''}
          key={id}
          onChange={name => {
            nameTouchedRef.current = true;
            nameRef.current = name;
          }}
          onSave={name => {
            if (project?.id) {
              patchProject({
                id: project.id,
                name,
              });
              return;
            }
            createProjectFromTitle(name);
          }}
          placeholder={project ? undefined : t('createAProject')}
          style={{
            fontSize: 24,
            fontWeight: 'bold',
            height: 40,
            marginBottom: 0,
            padding: 0,
            width: '100%',
          }}
        />
      </PaneHeader>
      <Tabs
        className="mb-3"
        defaultValue={initialTab}
        options={[
          ...(tasksEnabled ? [{
            content: (
              <TasksView
                defaultFilter={projectTasksFilter}
                extraFilter={project?.id ? { projects: project.id } : undefined}
                onTaskClick={
                  taskId => dispatch(
                    setPaneContent({
                      type: 'TASK',
                      id: taskId,
                    })
                  )
                }
                paginated
                projectId={project?.id}
                showAddButton
                showCompletionFilter
                showDueDateFilter
                skip={!project?.id}
              />
            ),
            label: `${t('tasks')} (${projectTasksIncompleteCount})`,
            value: 'tasks',
          }] : []),
          ...(objectivesEnabled ? [{
            content: (
              <ObjectivesView
                objectives={projectObjectives}
                onObjectiveClick={
                  objectiveId => dispatch(
                    setPaneContent({
                      type: 'OBJECTIVE',
                      id: objectiveId,
                    })
                  )
                }
                projectId={project?.id}
                showAddButton
                showCompletionFilter
                showDueDateFilter
                showViewModeButtons
              />
            ),
            label: `Objectifs (${projectObjectives?.length})`,
            value: 'objectives',
          }] : []),
          {
            content: (
              <ProjectMembers
                editedMembers={editedMembers}
                key={id}
                onMembersChange={setEditedMembers}
                project={project}
              />
            ),
            label: t('members'),
            value: 'members',
          },
          {
            content: (
              <ProjectStats
                projectId={project?.id}
              />
            ),
            label: t('stats'),
            value: 'stats',
          },
          {
            content: (
              <ProjectInformationsForm
                creatingRef={creatingRef}
                editedMembers={editedMembers}
                nameRef={nameRef}
                project={project}
              />
            ),
            label: t('informations'),
            value: 'infos',
          },
          {
            content: project && (
              <ProjectDiscussion
                project={project}
              />
            ),
            label: t('discussion'),
            value: 'DISCUSSION',
          },
        ]}
      />
    </>
  );
};

export default ProjectPane;
